import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PlayerCardsContextData } from "./PlayerCardsContext";
import { UserContextData } from "./UserContext";
import { Blog, ContentPage, Page, PlayerCardWidget, Post, Review, SmartLink } from "@/services/bedrock";
import { debounce } from "lodash";

export type RecordContextType = Post | ContentPage | SmartLink | Blog | Review;
export interface RecordContextData<T extends RecordContextType> {
  recordTypeLabel: string;
  records: T[];
  cachedRecords: { [id: number]: T };
  randomRecord: T | undefined;
  mostRecentRecord: T | undefined;
  isLoading: boolean;
  activePage: number;
  setActivePage: React.Dispatch<React.SetStateAction<number>>;
  totalPages: number;
  searchText: string | undefined;
  setSearchText: (search: string) => void;
}

export function createInitialRecordContextData<T extends RecordContextType>(): RecordContextData<T> {
  return {
    recordTypeLabel: "",
    records: [],
    cachedRecords: {},
    randomRecord: undefined,
    mostRecentRecord: undefined,
    isLoading: true,
    activePage: 1,
    setActivePage: () => {},
    totalPages: 1,
    searchText: undefined,
    setSearchText: () => {},
  };
}

export function createRecordContextData<T extends RecordContextType>(
  PlayerCards: PlayerCardsContextData,
  User: UserContextData,
  recordTypeLabel: string,
  fetchRecords: (playerId: number, page: number, size: number, search?: string) => Promise<Page<T>>,
  getRecordFromWidget: (widget: PlayerCardWidget) => T | null,
  isRecordNewer: (record: T, otherRecord?: T) => boolean,
  perPage = 15
): RecordContextData<T> {
  const [records, setRecords] = useState<T[]>([]);
  const [randomRecord, setRandomRecord] = useState<T>();
  const [mostRecentRecord, setMostRecentRecord] = useState<T>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [activePage, setActivePage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [searchText, setSearchText] = useState<string>();
  const playerId = User.playerId || PlayerCards.selectedPlayerCard?.playerId;
  const [cachedRecords, setCachedRecords] = useState<{ [id: number]: T }>({});
  const abortControllerRef = useRef<AbortController | null>(null);

  const updateCachedRecords = (records: T[]) => {
    setCachedRecords(prev => {
      const newRecords = { ...prev };
      records.forEach(record => {
        newRecords[Number(record.id)] = record;
      });
      return newRecords;
    });
  };

  const loadPage = useCallback(
    (page?: number, search?: string) => {
      if (playerId === undefined) {
        setRecords([]);
        return;
      }
      if (page !== undefined) setActivePage(page);
      else page = activePage;
      if (search !== undefined) setSearchText(search);
      else search = searchText;

      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setIsLoading(true);
      fetchRecords(playerId, page, perPage, search)
        .then(({ content: records, pagination: { totalPages } }) => {
          if (abortControllerRef.current !== controller) return;
          setRecords(records);
          updateCachedRecords(records);
          setTotalPages(totalPages ?? 1);
        })
        .catch(err => {
          if (err.name === "AbortError") return;
          console.error("Failed to fetch records", err);
        })
        .finally(() => {
          if (abortControllerRef.current === controller) {
            setIsLoading(false);
            abortControllerRef.current = null;
          }
        });
    },
    [activePage, playerId, searchText, fetchRecords, perPage]
  );

  useEffect(() => {
    loadPage();
    return () => abortControllerRef.current?.abort();
  }, [loadPage]);

  useEffect(() => {
    setActivePage(1);
    setSearchText(undefined);
  }, [playerId]);

  useEffect(() => {
    const playerCardOwnerRecords = (PlayerCards.selectedPlayerCard?.widgets ?? [])
      .map(getRecordFromWidget)
      .filter(Boolean) as T[];
    updateCachedRecords(playerCardOwnerRecords);
  }, [PlayerCards.selectedPlayerCard]);

  const debouncedSearch = useMemo(() => debounce((search: string) => loadPage(1, search), 500), [loadPage]);

  useEffect(loadPage, [activePage]);
  useEffect(() => {
    if (playerId === undefined) {
      setRandomRecord(undefined);
      setMostRecentRecord(undefined);
    }
    const playerRecords = Object.values(cachedRecords).filter(
      record => "playerId" in record && record.playerId === playerId
    ) as T[];
    setRandomRecord(prev => {
      if (prev && "playerId" in prev && prev?.playerId === playerId) return prev;
      return playerRecords[Math.floor(Math.random() * playerRecords.length)];
    });
    setMostRecentRecord(prev => {
      let newRecord = prev && "playerId" in prev && prev.playerId === playerId ? prev : undefined;
      playerRecords.forEach(record => {
        if ("playerId" in record && isRecordNewer(record, newRecord)) newRecord = record;
      });
      return newRecord;
    });
  }, [cachedRecords, playerId]);

  return {
    recordTypeLabel,
    records,
    cachedRecords,
    randomRecord,
    mostRecentRecord,
    isLoading,
    activePage,
    setActivePage,
    totalPages,
    searchText,
    setSearchText: debouncedSearch,
  };
}
